import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const formSchema = z.object({
  name: z.string().trim().min(2).max(12),
  surname: z.string().trim().min(2).max(12),
})

export type CharacterRenameValues = z.infer<typeof formSchema>

interface Props {
  character: wowaudit.CharacterReference
  isPending?: boolean
  onSubmit: (values: CharacterRenameValues) => void
  onClose: () => void
}

const CharacterRenameModal = ({
  character,
  isPending,
  onSubmit,
  onClose,
}: Props) => {
  const form = useForm<CharacterRenameValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: character.inGameName,
      surname: character.surname ?? '',
    },
  })

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change name</DialogTitle>
          <DialogDescription>
            This renames the character everywhere it is used on wowaudit.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            id='rename-character-form'
            className='grid grid-cols-2 gap-2'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input maxLength={12} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='surname'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Surname</FormLabel>
                  <FormControl>
                    <Input maxLength={12} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type='submit'
            form='rename-character-form'
            loading={isPending}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CharacterRenameModal
